import * as React from "react";
import formatDate from "dateformat";
import { transparentize } from "polished";
import { scroller } from "react-scroll";
import Actionable from "../../../../ui-library/actionable/actionable";
import { EventCategory } from "../../../../../utils/analytics";
import { ProcessedTimePoint } from "./process-timeline-data";
import styles from "./response-table-body.styles";
import { POLICY_VALUE_DETAIL } from "./policy-value-explaination";

interface ResponseTableBodyProps {
  countryName: string;
  policyTimeline: { [key: string]: ProcessedTimePoint[] };
  policyType: string;
  color: string;
  activePolicy?: ProcessedTimePoint;
  onClickPolicy: (policy: ProcessedTimePoint) => void;
}

const ResponseTableBody: React.FC<ResponseTableBodyProps> = ({
  countryName,
  policyTimeline,
  policyType,
  color,
  activePolicy,
  onClickPolicy,
}) => {
  return (
    <div css={styles.root}>
      {Object.keys(policyTimeline).map((k) => {
        const timeline = policyTimeline[k];
        const timelineWithAction = timeline.filter((t) => t.policyValue > 0);
        const startPoint = timelineWithAction[0]?.startPercent;
        const endPoint =
          timelineWithAction[timelineWithAction.length - 1]?.endPercent;

        const policy = POLICY_VALUE_DETAIL[`${policyType}${k}`];

        return (
          <div key={`${countryName}${policyType}${k}`} css={styles.policyRow}>
            <div css={styles.policyTitle}>{policy.title}</div>
            <div css={styles.barContainer}>
              {!!endPoint && (
                <div
                  css={styles.bar}
                  style={{
                    backgroundColor: transparentize(0.8, color),
                    left: `calc(${startPoint * 100}% - 10px)`,
                    right: `calc(${100 - endPoint * 100}% - 10px)`,
                  }}
                />
              )}
              {timeline.map((t, i) => (
                <Actionable
                  key={`${countryName}${policyType}${k}${t.startPercent}-${i}`}
                  role="button"
                  ariaLabel="Policy"
                  title={
                    policy.measurement === "ordinal"
                      ? `${formatDate(t.date, "mmm dd")}: Level ${
                          t.policyValue
                        } of ${policy.coding.length - 1}`
                      : formatDate(t.date, "mmm dd")
                  }
                  css={[
                    styles.dot,
                    {
                      left: `calc(${t.startPercent * 100}% - 10px)`,
                      "&::after": {
                        backgroundColor: color,
                      },
                    },
                    activePolicy?.policyType === t.policyType &&
                      activePolicy.date === t.date &&
                      styles.activeDot,
                  ]}
                  onClick={() => {
                    onClickPolicy(t);
                    scroller.scrollTo("govResTable", {
                      offset: -20,
                      smooth: true,
                    });
                  }}
                  analytics={{
                    category: EventCategory.CountryGovResponseVsCases,
                    action: "Click - Timepoint",
                    label: `${t.policyType} - ${t.date}`,
                  }}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ResponseTableBody;
